import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./styles/HybridCloudHosting.css";

const workloadTabs = [
  {
    key: "mission",
    label: "Mission Critical Apps",
    title: "Custom hybrid hosting for workloads that cannot afford instability",
    text: "ComNetworks builds custom hybrid cloud hosting solutions for mission critical applications, helping businesses place sensitive or high priority workloads in the environment that best supports security, continuity, and performance.",
    bullets: [
      "Custom hosting for critical business applications",
      "Flexible workload placement across environments",
      "Managed support aligned to operational needs",
    ],
  },
  {
    key: "networking",
    label: "Unified Networking",
    title: "A connected environment regardless of where workloads run",
    text: "Unified networking helps connect private, public, hybrid virtualized, and traditional managed server environments so teams can work across locations with more consistency and less fragmentation.",
    bullets: [
      "Unified networking regardless of location",
      "Better coordination across cloud and server environments",
      "Support for distributed infrastructure models",
    ],
  },
  {
    key: "migration",
    label: "Migration & Analysis",
    title: "Assess, convert, and migrate with a clearer path forward",
    text: "Cloud assessment and migration analysis help identify what should move, what should stay, and how existing software can be converted to cloud based platforms with less disruption.",
    bullets: [
      "Cloud assessment and migration analysis",
      "Conversion of existing software to cloud based platforms",
      "Structured modernization planning",
    ],
  },
  {
    key: "recovery",
    label: "Recovery & Resilience",
    title: "Recovery planning built around hybrid continuity needs",
    text: "Customized disaster recovery solutions help create stronger resilience across mixed environments where uptime, replication, and continuity planning are central to business operations.",
    bullets: [
      "Customized disaster recovery solutions",
      "Managed cloud hosting with resilience in mind",
      "Business continuity support across hybrid environments",
    ],
  },
];

const hostingModels = [
  {
    title: "Managed Public Cloud",
    text: "Support scalable workloads with managed services in public cloud environments where agility and elasticity matter.",
  },
  {
    title: "Managed Private Cloud",
    text: "Use more controlled environments for workloads that need stronger governance, predictable performance, or tighter control.",
  },
  {
    title: "Hybrid Virtualized Environments",
    text: "Bridge private and public resources with virtualization strategies that help standardize hosting and operations.",
  },
  {
    title: "Traditional Managed Servers",
    text: "Maintain support for workloads that still fit best on traditional managed infrastructure while planning future cloud alignment.",
  },
];

const pathwaySteps = [
  {
    step: "01",
    title: "Understand the workload",
    text: "Start with business requirements, technical dependencies, and operational priorities instead of forcing every system into one model.",
  },
  {
    step: "02",
    title: "Assess cloud readiness",
    text: "Use assessment and migration analysis to determine what can move, what needs redesign, and what should remain where it is.",
  },
  {
    step: "03",
    title: "Design the hybrid mix",
    text: "Combine managed public, private, virtualized, and traditional hosting options around the workload’s real needs.",
  },
  {
    step: "04",
    title: "Support resilience and growth",
    text: "Add unified networking, managed hosting, and customized disaster recovery planning to support continuity over time.",
  },
];

const fitCards = [
  {
    title: "Organizations with mixed workloads",
    text: "For teams running a blend of legacy platforms, modern cloud applications, and business critical systems.",
  },
  {
    title: "Businesses planning cloud modernization",
    text: "For companies that need migration analysis, software conversion planning, and a structured path into cloud environments.",
  },
  {
    title: "Operations with continuity priorities",
    text: "For environments where disaster recovery, uptime, and workload resilience are essential to the business.",
  },
  {
    title: "Teams avoiding one size fits all hosting",
    text: "For organizations that need the flexibility to choose public, private, hybrid virtualized, or traditional servers by use case.",
  },
];

export default function HybridCloudHosting() {
  const [activeTab, setActiveTab] = useState(workloadTabs[0]);

  const tabButtons = useMemo(
    () =>
      workloadTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`hybTabBtn ${activeTab.key === tab.key ? "active" : ""}`}
          onClick={() => setActiveTab(tab)}
        >
          {tab.label}
        </button>
      )),
    [activeTab]
  );

  return (
    <div className="hybPage">
      <section className="hybHero">
        <div className="hybContainer hybHero__container">
          <div className="hybHero__content">
            <p className="hybEyebrow">Hybrid Cloud Hosting</p>

            <h1 className="hybHero__title">
              Hosting decisions built around workloads, not one fixed model.
            </h1>

            <p className="hybHero__text">
              ComNetworks works to understand your business and build the right
              managed hybrid solution for each workload, including mission critical
              application hosting, unified networking, software conversion,
              customized disaster recovery, managed cloud hosting, migration
              analysis, and fully managed public, private, hybrid virtualized,
              or traditional server environments.
            </p>

            <div className="hybHero__actions">
              <Link to="/contact" className="hybBtn hybBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="hybBtn hybBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="hybHero__visual">
            <div className="hybOrchestrator">
              <div className="hybOrchestrator__top">
                <span className="hybPill">Workload aware</span>
                <span className="hybPill hybPill--soft">Managed hybrid</span>
              </div>

              <div className="hybOrchestrator__board">
                <div className="hybNode hybNode--core">Hybrid Control</div>
                <div className="hybNode hybNode--public">Public</div>
                <div className="hybNode hybNode--private">Private</div>
                <div className="hybNode hybNode--virtual">Virtualized</div>
                <div className="hybNode hybNode--server">Traditional</div>

                <span className="hybLink hybLink--one" />
                <span className="hybLink hybLink--two" />
                <span className="hybLink hybLink--three" />
                <span className="hybLink hybLink--four" />
              </div>

              <div className="hybOrchestrator__bottom">
                <div className="hybMetric">
                  <strong>Unified placement</strong>
                  <span>Choose where workloads belong based on real operating needs</span>
                </div>
                <div className="hybMetric">
                  <strong>Recovery aligned</strong>
                  <span>Design hosting with continuity and DR in mind</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="hybTabsSection">
        <div className="hybContainer">
          <div className="hybSectionHead hybSectionHead--center">
            <p className="hybEyebrow">Workload views</p>
            <h2 className="hybTitle">
              Explore the hybrid hosting service through key workload needs
            </h2>
            <p className="hybText">
              This page uses a workload first structure so visitors can scan by
              real business need instead of reading one long undifferentiated list.
            </p>
          </div>

          <div className="hybTabsShell">
            <div className="hybTabsNav">{tabButtons}</div>

            <div className="hybTabsContent">
              <div className="hybTabsContent__text">
                <p className="hybTabsLabel">{activeTab.label}</p>
                <h3>{activeTab.title}</h3>
                <p>{activeTab.text}</p>
              </div>

              <div className="hybTabsContent__bullets">
                {activeTab.bullets.map((item) => (
                  <div key={item} className="hybBulletCard">
                    <span className="hybBulletCard__mark" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="hybModelsSection">
        <div className="hybContainer">
          <div className="hybSectionHead">
            <p className="hybEyebrow">Hosting models</p>
            <h2 className="hybTitle">
              Multiple hosting environments, each suited to a different role
            </h2>
            <p className="hybText">
              Hybrid cloud becomes more practical when businesses can choose the
              right model for each workload instead of treating all infrastructure
              the same way.
            </p>
          </div>

          <div className="hybModelsGrid">
            {hostingModels.map((item) => (
              <article key={item.title} className="hybModelCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hybPathSection">
        <div className="hybContainer">
          <div className="hybSectionHead hybSectionHead--center">
            <p className="hybEyebrow">Delivery path</p>
            <h2 className="hybTitle">
              A different section style: a hosting pathway instead of another card row
            </h2>
            <p className="hybText">
              This section uses a horizontal step system to make the page feel
              distinct from the other service layouts while still explaining the process clearly.
            </p>
          </div>

          <div className="hybPathGrid">
            {pathwaySteps.map((item) => (
              <article key={item.step} className="hybStepCard">
                <span className="hybStepCard__step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hybBandSection">
        <div className="hybContainer">
          <div className="hybBand">
            <div className="hybBand__left">
              <p className="hybEyebrow">Networking and recovery</p>
              <h2 className="hybTitle">
                Unified networking and customized DR are central to hybrid success
              </h2>
            </div>

            <div className="hybBand__right">
              <p className="hybText">
                Hybrid environments are not only about where workloads run. They
                also depend on how environments connect, how failover is planned,
                and how managed hosting supports ongoing continuity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="hybFitSection">
        <div className="hybContainer">
          <div className="hybSectionHead hybSectionHead--center">
            <p className="hybEyebrow">Best fit</p>
            <h2 className="hybTitle">
              Where Hybrid Cloud Hosting creates the strongest value
            </h2>
            <p className="hybText">
              This model is especially useful for businesses with varied
              application types, different infrastructure constraints, and
              stronger continuity requirements.
            </p>
          </div>

          <div className="hybFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="hybFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hybCta">
        <div className="hybContainer hybCta__container">
          <div className="hybCta__content">
            <p className="hybEyebrow hybEyebrow--light">Next step</p>
            <h2 className="hybCta__title">
              Build a hosting strategy that matches each workload more intelligently
            </h2>
            <p className="hybCta__text">
              If you need managed hybrid hosting, migration analysis, unified
              networking, software conversion, or disaster recovery planning,
              ComNetworks can help define the right mix of environments.
            </p>
          </div>

          <div className="hybCta__actions">
            <Link to="/contact" className="hybBtn hybBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="hybBtn hybBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
